import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Stethoscope, Search, MapPin, Globe, BadgeCheck, Bookmark, Star, ExternalLink,
  Navigation, Phone, Loader2, ShieldCheck, Building2, Trash2,
} from "lucide-react";

const RADII = [5, 10, 25, 50];
const STATUS_META = {
  saved: { key: "fh.saved", cls: "bg-muted text-muted-foreground" },
  contacted: { key: "fh.contacted", cls: "bg-primary/15 text-primary" },
  booked: { key: "fh.booked", cls: "bg-sage/15 text-sage" },
};

function Stars({ rating, reviews }) {
  if (!rating) return null;
  return (
    <span className="flex items-center gap-1 text-primary">
      <Star className="h-3.5 w-3.5 fill-current" strokeWidth={1.5} /> {rating}
      {reviews ? <span className="text-muted-foreground">({reviews})</span> : null}
    </span>
  );
}

export default function Professionals() {
  const { t } = useI18n();
  const [tab, setTab] = useState("search");
  const [taxonomy, setTaxonomy] = useState({ professions: [], specializations: [] });
  const [filters, setFilters] = useState({ profession: "", specialization: "", q: "", city: "", country: "", radius_km: 25 });
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ curated: [], nearby: [], live_available: true, live_error: null });
  const [saved, setSaved] = useState([]);

  const savedKey = (source, ref) => `${source}:${ref}`;
  const savedSet = new Set(saved.map((s) => savedKey(s.source, s.ref_id)));
  const savedByKey = Object.fromEntries(saved.map((s) => [savedKey(s.source, s.ref_id), s]));

  const loadSaved = useCallback(() => {
    api.get("/find-help/saved").then(({ data }) => setSaved(data.saved || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/find-help/taxonomy").then(({ data }) => setTaxonomy(data)).catch(() => {});
    loadSaved();
  }, [loadSaved]);

  const search = useCallback(async (f, c) => {
    setLoading(true);
    const params = { ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) };
    if (c) { params.lat = c.lat; params.lng = c.lng; }
    try {
      const { data } = await api.get("/find-help/search", { params });
      setData(data);
    } catch { toast.error("Couldn't load results. Please try again."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(filters, coords), 400);
    return () => clearTimeout(t);
  }, [filters, coords, search]);

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location isn't available in this browser."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); toast.success("Showing specialists near you"); },
      () => { setLocating(false); toast.error("Location permission denied. Enter your city or country instead."); },
      { enableHighAccuracy: false, timeout: 8000 },
    );
  };

  const toggleSave = async (payload) => {
    const key = savedKey(payload.source, payload.ref_id);
    const existing = savedByKey[key];
    if (existing) {
      setSaved((s) => s.filter((x) => x.save_id !== existing.save_id));
      try { await api.delete(`/find-help/saved/${existing.save_id}`); toast(t("fh.removedToast")); } catch {}
      loadSaved();
    } else {
      try {
        const { data } = await api.post("/find-help/saved", payload);
        toast.success(t("fh.savedToast"));
        if (data.item) setSaved((s) => [data.item, ...s]); else loadSaved();
      } catch { toast.error("Couldn't save that."); }
    }
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const SaveButton = ({ payload }) => {
    const isSaved = savedSet.has(savedKey(payload.source, payload.ref_id));
    return (
      <button onClick={() => toggleSave(payload)} data-testid={`save-${payload.source}-${payload.ref_id}`}
              className={isSaved ? "text-primary" : "text-muted-foreground hover:text-foreground"} title={isSaved ? t("fh.saved") : "Save"}>
        <Bookmark className={`h-5 w-5 ${isSaved ? "fill-current" : ""}`} strokeWidth={1.5} />
      </button>
    );
  };

  return (
    <div data-testid="find-help-page">
      <h1 className="font-serif text-3xl font-semibold sm:text-4xl">{t("fh.title")}</h1>
      <p className="mt-2 max-w-xl text-muted-foreground">
        {t("fh.subtitle")}
      </p>

      {/* Tabs */}
      <div className="mt-6 inline-flex rounded-full bg-muted/60 p-1" data-testid="find-help-tabs">
        <button data-testid="tab-search" onClick={() => setTab("search")}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${tab === "search" ? "bg-card text-foreground shadow" : "text-muted-foreground"}`}>
          {t("fh.tabSearch")}
        </button>
        <button data-testid="tab-list" onClick={() => { setTab("list"); loadSaved(); }}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${tab === "list" ? "bg-card text-foreground shadow" : "text-muted-foreground"}`}>
          {t("fh.tabList")}{saved.length ? ` (${saved.length})` : ""}
        </button>
      </div>

      {tab === "search" ? (
        <>
          {/* Filters */}
          <div className="mt-6 space-y-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <Select value={filters.profession || "any"} onValueChange={(v) => set("profession", v === "any" ? "" : v)}>
                <SelectTrigger className="rounded-full" data-testid="find-help-profession"><SelectValue placeholder={t("fh.profession")} /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="any">{t("fh.anyProfession")}</SelectItem>
                  {taxonomy.professions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.specialization || "any"} onValueChange={(v) => set("specialization", v === "any" ? "" : v)}>
                <SelectTrigger className="rounded-full" data-testid="find-help-specialization"><SelectValue placeholder={t("fh.specialization")} /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="any">{t("fh.anyFocus")}</SelectItem>
                  {taxonomy.specializations.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                <Input value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder={t("fh.keyword")} data-testid="find-help-search-input" className="rounded-full pl-9" />
              </div>
              <Select value={String(filters.radius_km)} onValueChange={(v) => set("radius_km", Number(v))}>
                <SelectTrigger className="rounded-full" data-testid="find-help-radius"><SelectValue /></SelectTrigger>
                <SelectContent>{RADII.map((r) => <SelectItem key={r} value={String(r)}>{`${t("fh.within")} ${r} km`}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <Input value={filters.city} onChange={(e) => set("city", e.target.value)} placeholder={t("fh.city")} data-testid="find-help-city" className="rounded-full" />
              <Input value={filters.country} onChange={(e) => set("country", e.target.value)} placeholder={t("fh.country")} data-testid="find-help-country" className="rounded-full" />
              <Button variant={coords ? "default" : "outline"} className="rounded-full gap-2" onClick={useMyLocation} disabled={locating} data-testid="find-help-use-location">
                {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" strokeWidth={1.5} />}
                {coords ? t("fh.usingLocation") : t("fh.useLocation")}
              </Button>
            </div>
          </div>

          {loading && <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t("fh.searching")}</div>}

          {/* Curated */}
          {data.curated.length > 0 && (
            <section className="mt-8">
              <div className="mb-3 flex items-center gap-2"><BadgeCheck className="h-5 w-5 text-sage" strokeWidth={1.5} /><h2 className="font-serif text-xl">{t("fh.verifiedTitle")}</h2></div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {data.curated.map((p, i) => (
                  <motion.div key={p.pro_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.04 }}
                              className="ambient-shadow rounded-3xl border border-border/60 bg-card p-6" data-testid={`curated-card-${p.pro_id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10"><Stethoscope className="h-6 w-6 text-primary" strokeWidth={1.5} /></div>
                        <div><div className="flex items-center gap-1.5"><span className="font-serif text-lg">{p.name}</span>{p.verified && <BadgeCheck className="h-4 w-4 text-sage" strokeWidth={1.5} />}</div><p className="text-sm text-muted-foreground">{p.profession}</p></div>
                      </div>
                      <SaveButton payload={{ source: "curated", ref_id: p.pro_id, name: p.name, subtitle: p.profession, city: p.city, country: p.country, rating: p.rating, website: p.website }} />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-1.5">{(p.specialties || []).slice(0, 3).map((s) => <Badge key={s} variant="secondary" className="rounded-full text-xs">{s}</Badge>)}</div>
                    <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" strokeWidth={1.5} /> {p.city}, {p.country}</span>
                      {(p.languages || []).length > 0 && <span className="flex items-center gap-1"><Globe className="h-3 w-3" strokeWidth={1.5} /> {(p.languages || []).join(", ")}</span>}
                      <Stars rating={p.rating} />
                    </div>
                    {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"><ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("fh.visitWebsite")}</a>}
                  </motion.div>
                ))}
              </div>
            </section>
          )}

          {/* Nearby (Google) */}
          <section className="mt-8">
            <div className="mb-1 flex items-center gap-2"><MapPin className="h-5 w-5 text-primary" strokeWidth={1.5} /><h2 className="font-serif text-xl">{t("fh.nearbyTitle")}</h2></div>
            <p className="mb-3 text-xs text-muted-foreground">{t("fh.nearbyDisclaimer")}</p>
            {!data.live_available && !loading && (
              <div className="rounded-2xl border border-border/60 bg-card/50 p-5 text-sm text-muted-foreground" data-testid="nearby-unavailable">
                {t("fh.nearbyUnavailable")}
              </div>
            )}
            {data.live_error && <div className="rounded-2xl border border-border/60 bg-card/50 p-5 text-sm text-muted-foreground">{data.live_error}</div>}
            {data.live_available && data.nearby.length === 0 && !loading && !data.live_error && (
              <p className="text-sm text-muted-foreground">{t("fh.nearbyNone")}</p>
            )}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {data.nearby.map((p, i) => (
                <motion.div key={p.place_id || i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.03 }}
                            className="ambient-shadow rounded-3xl border border-border/60 bg-card p-6" data-testid={`nearby-card-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary"><Building2 className="h-6 w-6 text-secondary-foreground" strokeWidth={1.5} /></div>
                      <div className="min-w-0 flex-1"><span className="font-serif text-lg">{p.name}</span>{p.address && <p className="mt-0.5 text-sm text-muted-foreground">{p.address}</p>}</div>
                    </div>
                    <SaveButton payload={{ source: "google", ref_id: p.place_id, name: p.name, subtitle: t("fh.specialist"), address: p.address, phone: p.phone, website: p.website, maps_url: p.maps_url, rating: p.rating }} />
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                    <Stars rating={p.rating} reviews={p.reviews} />
                    {p.distance_km != null && <span className="flex items-center gap-1"><Navigation className="h-3 w-3" strokeWidth={1.5} /> {p.distance_km} {t("fh.kmAway")}</span>}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {p.phone && <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-xs hover:bg-accent/60" data-testid={`nearby-call-${i}`}><Phone className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("fh.call")}</a>}
                    {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-xs hover:bg-accent/60"><ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("fh.website")}</a>}
                    {p.maps_url && <a href={p.maps_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-xs hover:bg-accent/60" data-testid={`nearby-directions-${i}`}><MapPin className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("fh.directions")}</a>}
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        </>
      ) : (
        <CareList saved={saved} setSaved={setSaved} reload={loadSaved} />
      )}

      <p className="mt-10 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("fh.crisisNote")}
      </p>
    </div>
  );
}

function CareList({ saved, setSaved, reload }) {
  const { t } = useI18n();
  const updateStatus = async (item, status) => {
    setSaved((s) => s.map((x) => (x.save_id === item.save_id ? { ...x, status } : x)));
    try { await api.patch(`/find-help/saved/${item.save_id}`, { status }); } catch { toast.error("Couldn't update status"); reload(); }
  };
  const saveNote = async (item, note) => {
    if (note === (item.note || "")) return;
    try { await api.patch(`/find-help/saved/${item.save_id}`, { note }); toast.success("Note saved"); } catch { toast.error("Couldn't save note"); }
  };
  const remove = async (item) => {
    setSaved((s) => s.filter((x) => x.save_id !== item.save_id));
    try { await api.delete(`/find-help/saved/${item.save_id}`); } catch {}
  };

  if (saved.length === 0) {
    return (
      <div className="mt-10 flex flex-col items-center justify-center rounded-3xl border border-dashed border-border/60 py-16 text-center" data-testid="care-list-empty">
        <Bookmark className="h-8 w-8 text-muted-foreground" strokeWidth={1.5} />
        <p className="mt-3 font-serif text-xl">{t("fh.careEmptyTitle")}</p>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">{t("fh.careEmptyDesc")}</p>
      </div>
    );
  }

  return (
    <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="care-list">
      {saved.map((item, i) => (
        <motion.div key={item.save_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.04 }}
                    className="ambient-shadow rounded-3xl border border-border/60 bg-card p-6" data-testid={`care-card-${item.save_id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-serif text-lg">{item.name}</span>
                <Badge className={`rounded-full text-[10px] ${STATUS_META[item.status]?.cls || ""}`}>{STATUS_META[item.status] ? t(STATUS_META[item.status].key) : item.status}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">{item.subtitle || (item.source === "google" ? t("fh.specialist") : "")}{item.city ? ` · ${item.city}` : ""}{item.country ? `, ${item.country}` : ""}</p>
              {item.address && <p className="mt-0.5 text-xs text-muted-foreground">{item.address}</p>}
            </div>
            <button onClick={() => remove(item)} data-testid={`care-remove-${item.save_id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" strokeWidth={1.5} /></button>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {item.phone && <a href={`tel:${item.phone}`} className="inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-xs hover:bg-accent/60"><Phone className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("fh.call")}</a>}
            {item.website && <a href={item.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-xs hover:bg-accent/60"><ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("fh.website")}</a>}
            {item.maps_url && <a href={item.maps_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-xs hover:bg-accent/60"><MapPin className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("fh.directions")}</a>}
          </div>

          <div className="mt-4 flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{t("fh.status")}</span>
            <Select value={item.status} onValueChange={(v) => updateStatus(item, v)}>
              <SelectTrigger className="h-8 w-36 rounded-full text-xs" data-testid={`care-status-${item.save_id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="saved">{t("fh.saved")}</SelectItem>
                <SelectItem value="contacted">{t("fh.contacted")}</SelectItem>
                <SelectItem value="booked">{t("fh.booked")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Textarea defaultValue={item.note || ""} onBlur={(e) => saveNote(item, e.target.value)}
                    placeholder={t("fh.notePh")}
                    data-testid={`care-note-${item.save_id}`}
                    className="mt-3 min-h-[60px] resize-none rounded-2xl text-sm" />
        </motion.div>
      ))}
    </div>
  );
}
